import React, { useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import toaster from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { usePusherChannel } from 'services/pusher';
import {
  KeyDetails,
  PickupDeliveryDetails,
  PaymentDetails,
  Payments,
  OrderTimeline,
  OrderPipelineHistory,
  OrderSummary,
  CustomerDetails,
  Processing,
  Notification,
  StoreDetails,
  EditModal,
  EditOrderStatusModal,
} from 'components/admin/orders/components/';
// Components
import ConfirmationModal from 'components/common/ConfirmationModal';
import NotFound from 'components/common/NotFound/NotFound';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import { LIVELINK_ORDER_PAGE } from 'utils/config';
// Constants
import { PAYMENT_PROCESSOR, orderDeliveryStatuses } from 'utils/constants';
// Utils
import { getIsNotFound } from 'utils/getIsNotFound';
import { incrementalIdEncrypt } from 'utils/incrementalIdEncrypt';
import { updateOrderDeliveryStatus } from 'api/admin/orderDeliveries/orderDeliveries';
// APIs
import {
  getIndividualServiceOrder,
  updateServiceOrderStatus,
  getOrderLiveLink,
} from 'api/admin/orders/orders';
import {
  refundCashPayment,
  refundStripePayment,
  captureStripePayment,
} from 'api/admin/payments/payments';

const IndividualServiceOrder = () => {
  const { id } = useParams();
  const [serviceOrder, setServiceOrder] = useState({});
  const [error, setError] = useState();
  const [loading, setLoading] = useState(true);
  const [modalType, setModalType] = useState(null);
  const [paymentToUpdate, setPaymentToUpdate] = useState(null);
  const [paymentUpdateAction, setPaymentUpdateAction] = useState(null);
  const [deliveryModalType, setDeliveryModalType] = useState(null);
  const [deliveryToEdit, setDeliveryToEdit] = useState(null);
  const [isNotFound, setIsNotFound] = useState(false);

  const channel = usePusherChannel(
    serviceOrder.store?.id ? `private-store-${serviceOrder.store?.id}` : null,
  );
  const waitForPaymentCapture = useCallback(
    (paymentToCapture) => {
      return new Promise((resolve, reject) => {
        if (channel) {
          channel.bind('payment-intent-succeeded', (data) => {
            if (data.paymentId === paymentToCapture.id) resolve(data);
          });
          channel.bind('payment-intent-failed', (data) => {
            reject(data);
          });
        } else reject(new Error('Channel not found'));
      });
    },
    [channel],
  );

  // state for updated OrderDelivery fields
  const [updatedDeliveryStatus, setUpdatedDeliveryStatus] = useState();

  const processHttpError = (error) => {
    setError(error.response?.data?.error || error.message);
    setLoading(false);
  };

  /**
   * Retrieve order details using incoming URL params
   */
  const getIndividualOrder = useCallback(async () => {
    try {
      const serviceOrderData = await getIndividualServiceOrder(id);
      setServiceOrder(serviceOrderData.data.serviceOrder);
      setIsNotFound(false);
      setLoading(false);
    } catch (error) {
      if (getIsNotFound(error)) {
        setIsNotFound(true);
      } else {
        toaster('An error occurred while fetching service order', {
          icon: '❌',
        });
      }
      processHttpError(error);
    }
  }, [id]);

  useEffect(() => {
    getIndividualOrder();
  }, [getIndividualOrder]);

  /**
   * Open the delivery modal
   *
   * @param {String} modalType
   * @param {Object} delivery
   */
  const openDeliveryModal = (modalType, delivery) => {
    setDeliveryToEdit(delivery);
    setDeliveryModalType(modalType.toLowerCase());
  };

  /**
   * Update delivery status based on new selection or selection changes
   *
   * @param {Object} event
   */
  const handleDeliveryStatusChange = (event) => {
    setError(null);
    setUpdatedDeliveryStatus(event?.value);
  };

  /**
   * Update the ServiceOrder object with the new status selection
   */
  const saveUpdatedStatus = async ({
    updatedStatus,
    overridePaymentValidation = false,
    cancellationDetails,
  }) => {
    try {
      setLoading(true);

      const payload = {
        serviceOrderId: serviceOrder.id,
        status: updatedStatus,
        overridePaymentValidation,
      };
      if (cancellationDetails) {
        payload.cancellationDetails = cancellationDetails;
      }

      const updatedServiceOrderData = await updateServiceOrderStatus(payload);

      setServiceOrder(updatedServiceOrderData.data.serviceOrder);

      setModalType(null);

      setLoading(false);
    } catch (e) {
      processHttpError(e);
    }
  };

  /**
   * Determine the proper function to return for updating payments
   *
   * @param {Object} payment
   */
  const handlePaymentUpdate = (payment) => {
    switch (paymentUpdateAction) {
      case 'REFUND':
        return processCashRefund(payment);
      case 'CAPTURE':
        return capturePaymentInStripe(payment);
      default:
        break;
    }
  };

  /**
   * Process a cash refund for a given payment.
   *
   * This will perform the following:
   *
   * 1) Create a Refund model entry for the given payment;
   * 2) Change the status of the individual payment to "refunded";
   * 3) Set the paymentStatus of the ServiceOrder to BALANCE_DUE;
   * 4) Update the balanceDue of the ServiceOrder
   *
   * @param {Object} payment
   */
  const processCashRefund = async (payment) => {
    try {
      setLoading(true);

      if (payment.paymentProcessor === PAYMENT_PROCESSOR.CASH) {
        await refundCashPayment(payment.id);
      } else if (payment.paymentProcessor === PAYMENT_PROCESSOR.STRIPE) {
        await refundStripePayment(payment.id);
      } else {
        setPaymentToUpdate(null);
        setPaymentUpdateAction(null);
        return;
      }

      setPaymentToUpdate(null);
      setPaymentUpdateAction(null);
      setError(null);

      return getIndividualOrder();
    } catch (e) {
      setPaymentToUpdate(null);
      setPaymentUpdateAction(null);
      processHttpError(e);
    }
  };

  /**
   * Capture Stripe payment and charge the customer
   *
   * @param {Object} payment
   */
  const capturePaymentInStripe = async (payment) => {
    try {
      setLoading(true);

      await captureStripePayment(payment.id);

      setPaymentToUpdate(null);
      setPaymentUpdateAction(null);
      setError(null);

      await Promise.race([
        waitForPaymentCapture(payment),
        new Promise((resolve, reject) => {
          setTimeout(() => reject(new Error('Payment capture timeout')), 5000);
        }),
      ])
        .then(() => {
          return getIndividualOrder();
        })
        .catch((error) => {
          setError(error?.message);
          setLoading(false);
        });
    } catch (e) {
      processHttpError(e);
    }
  };

  /**
   * Update the OrderDelivery object with the new status selection
   */
  const saveUpdatedDeliveryStatus = async () => {
    try {
      setLoading(true);

      const payload = {
        orderDeliveryId: deliveryToEdit.id,
        status: updatedDeliveryStatus,
      };
      await updateOrderDeliveryStatus(payload);

      setDeliveryModalType(null);
      setDeliveryToEdit(null);

      return getIndividualOrder();
    } catch (error) {
      processHttpError(error);
    }
  };

  /**
   * Render a confirmation modal for given "one click" actions
   */
  const renderConfirmationModal = () => {
    let header = null;

    switch (paymentUpdateAction) {
      case 'REFUND':
        header = 'Are you sure you want to refund this payment?';
        break;
      case 'CAPTURE':
        header = `
          Are you sure you want to capture this payment?.
          The customer will be charged $${Number(paymentToUpdate.totalAmount).toFixed(2)}.
        `;
        break;
      default:
        break;
    }

    return (
      <ConfirmationModal
        onClose={() => {
          setPaymentToUpdate(null);
          setPaymentUpdateAction(null);
        }}
        header={header}
        submitAction={() => handlePaymentUpdate(paymentToUpdate)}
        isOpen={Boolean(paymentToUpdate)}
      />
    );
  };

  const manageOnBehalfOf = async () => {
    try {
      const { data } = await getOrderLiveLink(serviceOrder?.id);

      const newWindow = window.open(data.url, '_blank', 'noopener,noreferrer');

      if (newWindow) newWindow.opener = null;
    } catch (error) {
      toaster('An error occurred while redirecting to the manage order page', {
        icon: '❌',
      });
      Sentry.captureException(error);
    }
  };

  if (loading) return <CentsLoader />;

  if (isNotFound) return <NotFound message={error} />;

  if (!serviceOrder?.id) return null;

  return (
    <div className="page-container">
      <div className="service-order-page-container">
        <div className="service-order-header-container">
          <h1 className="service-order-header">Service Order: Code {serviceOrder.orderCode}</h1>
          <button className="manage-order-button" onClick={manageOnBehalfOf}>
            MANAGE ORDER
          </button>
        </div>
        <div className="service-order-content">
          <KeyDetails data={serviceOrder} onEdit={setModalType} isService />
          {serviceOrder.order?.allPickups
            ?.slice(0)
            ?.reverse()
            ?.map((pickup) => (
              <PickupDeliveryDetails
                key={pickup?.id}
                data={pickup}
                title="Pickup"
                onEdit={openDeliveryModal}
              />
            ))}
          {serviceOrder.order?.allReturnDeliveries
            ?.slice(0)
            ?.reverse()
            ?.map((delivery) => (
              <PickupDeliveryDetails
                key={delivery?.id}
                data={delivery}
                title="Delivery"
                onEdit={openDeliveryModal}
              />
            ))}
          <PaymentDetails data={serviceOrder} />
          <Payments
            payments={serviceOrder.order?.payments}
            onHeaderAction={(payment, actionType) => {
              setPaymentToUpdate(payment);
              setPaymentUpdateAction(actionType);
            }}
            isService
          />
          <OrderTimeline data={serviceOrder} />
          <OrderPipelineHistory serviceOrderId={serviceOrder.id} />
          <OrderSummary data={serviceOrder} isService />
          <CustomerDetails data={serviceOrder.centsCustomer} />
          <Processing data={serviceOrder} />
          <Notification data={serviceOrder.notificationLogs} />
          <StoreDetails data={serviceOrder.store} />
        </div>
      </div>
      <EditOrderStatusModal
        isOpen={modalType === 'status'}
        onClose={() => setModalType(null)}
        currentValue={serviceOrder.status}
        onSave={saveUpdatedStatus}
        error={error}
        setError={setError}
      />
      <EditModal
        onClose={() => {
          setDeliveryToEdit(null);
          setDeliveryModalType(null);
        }}
        modalType={deliveryModalType}
        currentValue={deliveryToEdit?.status}
        updatedValue={updatedDeliveryStatus}
        handleValueChange={handleDeliveryStatusChange}
        inputType="select"
        selectOptions={orderDeliveryStatuses}
        onSave={saveUpdatedDeliveryStatus}
        error={error}
      />
      {paymentToUpdate && renderConfirmationModal()}
    </div>
  );
};

export default IndividualServiceOrder;
